import { inject, Injectable } from '@angular/core'
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http'
import { injectQuery } from './query'
import { ToastService } from './toast.service'
import { LoggerService } from './logger.service'
import { User } from './users.service'
import { Team } from './teams.service'
import { Meeting, MeetingProcessingStatus, MeetingStatus, MeetingType, MetricType } from './meetings.service'
import { queryKeys } from './query-keys'
import { TimeInterval } from './common/time-interval/time-interval.component'
import { catchError, EMPTY, map, Observable, tap } from 'rxjs'
import { generateHeadersWithTimezone } from './utils'
import { DownloadsService } from './downloads.service'
import type { SupportUserActivityResult, UserActivityRow } from '../../../backend/src/metrics/support-user-activity-api.dto'
import type { SlaMetricsResult, SlaTeamBreakdown, SlaProcessingDistributionBucket } from '../../../backend/src/metrics/sla-api.dto'
import type { ActivityLogCoverage, ActivityLogDetails, ActivityLogEntry, ActivityLogFieldChange } from '../../../backend/src/metrics/activity-log-api.dto'
import type { SubscriptionUsageResult } from '../../../backend/src/metrics/subscription-usage-api.dto'

export type { SlaMetricsResult, SlaTeamBreakdown, SlaProcessingDistributionBucket }
export type { SubscriptionUsageResult }
export {
  MAX_USAGE_ALERT_THRESHOLD_COUNT,
  MAX_USAGE_ALERT_THRESHOLD_PERCENTAGE,
  MIN_USAGE_ALERT_THRESHOLD_PERCENTAGE,
} from '../../../backend/src/metrics/subscription-usage-api.dto'
export { ACTIVITY_LOG_AUTOMATIC_PERSON, ACTIVITY_LOG_SUPPORT_PERSON } from '../../../backend/src/metrics/activity-log-api.dto'
export type { ActivityLogCoverage, ActivityLogDetails, ActivityLogEntry, ActivityLogFieldChange }

export type AdminMetricQueryResult = {
  list: MetricDataPoint[]
  total: number
}

export type AdminCompletedMeetingsQueryResult = {
  list: CompletedMeetingDetails[]
  total: number
}

export enum MetricComponent {
  USER = 'USER',
  ASSISTANT = 'ASSISTANT',
  BACKEND = 'BACKEND',
  CALENDAR_SYNCHRONIZATION = 'CALENDAR_SYNCHRONIZATION',
  ASSISTANT_MANAGER = 'ASSISTANT_MANAGER',
  MEETING_DELETER = 'MEETING_DELETER',
}

export type MetricDataPoint = {
  id: number
  userId: number | null
  user: User | null
  meetingId: number | null
  meeting: Meeting | null
  teamId: number | null
  team: Team | null
  component: MetricComponent
  metric: MetricType
  metadata: any
  timestamp: Date
}

/**
 * What the usage row says about the user, which is all the endpoint sends: the table draws a name,
 * a link to the team and an active chip out of it, and the whole user row costs several
 * megabytes once every team of every client is asked about.
 */
export type UsageMetricUser = {
  id: number
  firstName: string
  lastName: string
  team: { id: number; name: string }
}

export type UsageByUserMetric = {
  user: UsageMetricUser
  usageByInterval: {
    intervalLabel: string
    intervalType: 'DAY' | 'MONTH'
    duration: number | null
    count: number | null
  }[]
  totalDuration: number | null
  totalCount: number | null
  active: boolean
}

export type RawUsageByUserMetric = {
  [userId: string]: UsageByUserMetric
}

export type UsageByTeamMetric = {
  team: Team
  activeUserCount: number
  usageByInterval: {
    intervalLabel: string
    intervalType: 'DAY' | 'MONTH'
    duration: number | null
    count: number | null
  }[]
}

export type RawUsageByTeamMetric = {
  [teamId: string]: UsageByTeamMetric
}

export function normalizeUsageByUserMetric(data: RawUsageByUserMetric): UsageByUserMetric[] {
  return Object.values(data).map((value) => ({
    user: value.user,
    usageByInterval: Object.entries(value.usageByInterval).map(([intervalLabel, data]: [string, any]) => ({
      intervalLabel,
      ...data,
    })),
    totalDuration: value.totalDuration,
    totalCount: value.totalCount,
    active: value.active,
  }))
}

export function normalizeTeamUsageMetric(data: RawUsageByTeamMetric): UsageByTeamMetric[] {
  return Object.values(data).map((value) => ({
    team: value.team,
    activeUserCount: value.activeUserCount,
    usageByInterval: Object.entries(value.usageByInterval).map(([intervalLabel, data]: [string, any]) => ({
      intervalLabel,
      ...data,
    })),
  }))
}

export type CompletedMeetingOwner = {
  userId: number
  firstName: string
  lastName: string
  email: string
  completedMeetingId: number
}

export type DeletionLifecycleStage = 'INITIAL_RETENTION' | 'SOFT_DELETED' | 'HARD_DELETED'

export type CompletedMeetingDetails = {
  meetingId: number
  name: string
  teamId: number
  duration: number | null
  actualStartDate: Date | null
  status: MeetingStatus
  type: MeetingType
  users: CompletedMeetingOwner[]
  latestMeetingSessionStatus: string | null
  processingStatus: MeetingProcessingStatus | null
  avgConfidenceScore: number | null
  avgRms: number | null
  avgHfRatio: number | null
  meetingRoomNames: string[]
  deletionLifecycleStage: DeletionLifecycleStage
}

export type FeatureAdoptionForUser = {
  userId: number
  firstName: string
  lastName: string
  hasUploadedAgenda: boolean
  hasLabelledSpeaker: boolean
  hasUsedEditor: boolean
  hasUsedAiActions: boolean
  hasEnabledSpeakerLabelingMode: boolean
  hasChangedMinutingStyles: boolean
  hasUsedJoinNow: boolean
  hasUsedPausing: boolean
}

export type MeetingScore = {
  meetingId: number
  name: string
  teamId: number
  avgConfidenceScore: number
}

export type MeetingRoomMetric = {
  id: number
  canonicalName: string
  teamId: number
  averageConfidenceScore: number | null
  averageRms: number | null
  averageHfRatio: number | null
  meetingCount: number
}

export type EngagementForUser = {
  userId: number
  firstName: string
  lastName: string
  meetingCount: number
  meetingsViewed: number
  meetingsEdited: number
  meetingsSpeakerLabeled: number
  meetingsExported: number
  averageSecondsToEdit: number
}

export type PerformanceMetric = {
  name: string
  sampleCount: number
  avgLatencyMs: number
  minLatencyMs: number
  maxLatencyMs: number
  p50LatencyMs: number
  p95LatencyMs: number
  p99LatencyMs: number
}

export type ActivityPrecision = 'HOUR' | 'DAY' | 'WEEK'

export type ActivityDataPoint = {
  intervalLabel: string
  activeUsers: number
  meetingViews: number
  appOpens: number
  editorSaves: number
}

export type ActivityMetricsResult = {
  precision: ActivityPrecision
  dataPoints: ActivityDataPoint[]
}

export type { SupportUserActivityResult, UserActivityRow }

@Injectable({
  providedIn: 'root',
})
export class MetricsService {
  httpClient = inject(HttpClient)
  query = injectQuery()
  toastService = inject(ToastService)
  loggerService = inject(LoggerService)
  downloadsService = inject(DownloadsService)

  constructor() {}

  sendMetric(meetingId: number | null, type: MetricType, metadata: object = {}) {
    const params = {
      type,
      meetingId,
      ...(Object.keys(metadata).length > 0 ? { metadata } : {}),
    }
    this.httpClient.post<void>(`/api/metrics`, params).subscribe({
      error: (err) => {
        this.loggerService.error(`Failed to send metric ${type} for meeting ${meetingId}.`, err)
      },
    })
  }

  getMetrics(
    userIds: number[],
    meetingId: number | null,
    teamId: number | null,
    component: MetricComponent | null,
    metrics: MetricType[],
    startDate: Date | null,
    endDate: Date | null,
    sortField: string | null,
    sortDirection: string | null,
    page: number,
    pageSize: number
  ) {
    const params = {
      ...(userIds.length > 0 ? { userId: userIds.map(String) } : {}),
      ...(meetingId ? { meetingId: meetingId.toString() } : {}),
      ...(teamId ? { teamId: teamId.toString() } : {}),
      ...(component ? { component } : {}),
      ...(metrics.length > 0 ? { metric: metrics } : {}),
      ...(startDate ? { startDate: startDate.toISOString() } : {}),
      ...(endDate ? { endDate: endDate.toISOString() } : {}),
      ...(sortField ? { sortField } : {}),
      ...(sortDirection ? { sortDirection } : {}),
      page,
      pageSize,
    }
    return this.query({
      queryKey: queryKeys.metrics.list(params),
      queryFn: () =>
        this.httpClient.get<AdminMetricQueryResult>('/api/metrics', {
          params,
        }),
    }).result$
  }

  /**
   * When each action the activity log reports on started being recorded, so an empty result can be
   * told apart from an action that never happened.
   */
  getActivityLogCoverage() {
    return this.query({
      queryKey: queryKeys.metrics.activityLogCoverage(),
      queryFn: () => this.httpClient.get<ActivityLogCoverage>('/api/metrics/activity-log/coverage'),
    }).result$
  }

  getUsageByUserMetrics(type: string, userId: number | null, teamIds: number[], exclusionFilters: string[], viewFilter: string | null, fromDate: Date | null) {
    const params = {
      ...(userId ? { userId } : {}),
      ...(teamIds !== null && teamIds.length > 0 ? { teamIds: teamIds } : {}),
      ...(exclusionFilters.length > 0 ? { exclusionFilters } : {}),
      ...(viewFilter ? { viewFilter } : {}),
      ...(fromDate ? { fromDateString: fromDate.toISOString() } : {}),
    }
    return this.query({
      queryKey: queryKeys.metrics.usageByUser(type, params),
      queryFn: () => this.httpClient.get<RawUsageByUserMetric>('/api/metrics/usage-by-user', { params }).pipe(map((data) => normalizeUsageByUserMetric(data))),
    }).result$
  }

  getUsageByTeamMetrics(teamIds: number[], fromDate: Date | null) {
    const params = {
      ...(teamIds !== null && teamIds.length > 0 ? { teamIds: teamIds } : {}),
      ...(fromDate ? { fromDateString: fromDate.toISOString() } : {}),
    }

    return this.query({
      queryKey: queryKeys.metrics.usageByTeam(params),
      queryFn: () => this.httpClient.get<any>('/api/metrics/usage-by-team', { params }).pipe(map((data) => normalizeTeamUsageMetric(data))),
    }).result$
  }

  getCompletedMeetings(
    userId: number | null,
    teamIds: number[],
    exclusionFilters: string[],
    viewFilter: string | null,
    timeInterval: TimeInterval,
    nameQuery: string | null,
    sortField: string | null,
    sortDirection: string | null,
    page: number,
    pageSize: number
  ) {
    const params = {
      ...(userId ? { userId: userId.toString() } : {}),
      ...(teamIds !== null && teamIds.length > 0 ? { teamIds: teamIds } : {}),
      ...(exclusionFilters.length > 0 ? { exclusionFilters } : {}),
      ...(timeInterval.from ? { fromDateString: timeInterval.from.toISOString() } : {}),
      ...(timeInterval.to ? { toDateString: timeInterval.to.toISOString() } : {}),
      ...(viewFilter ? { viewFilter } : {}),
      ...(nameQuery ? { nameQuery } : {}),
      ...(sortField ? { sortField } : {}),
      ...(sortDirection ? { sortDirection } : {}),
      page,
      pageSize,
    }
    return this.query({
      queryKey: queryKeys.metrics.completedMeetings(params),
      queryFn: () =>
        this.httpClient.get<AdminCompletedMeetingsQueryResult>('/api/metrics/completed-meetings', {
          params,
        }),
    }).result$
  }

  getFeatureAdoptionByUser(teamIds: number[]) {
    return this.query({
      queryKey: queryKeys.metrics.featureAdoptionByUser(teamIds),
      queryFn: () =>
        this.httpClient.get<FeatureAdoptionForUser[]>('/api/metrics/feature-adoption-by-user', {
          params: { teamIds },
        }),
    }).result$
  }

  getEngagementByUser(teamIds: number[]) {
    return this.query({
      queryKey: queryKeys.metrics.engagementByUser(teamIds),
      queryFn: () =>
        this.httpClient.get<EngagementForUser[]>('/api/metrics/engagement-by-user', {
          params: { teamIds },
        }),
    }).result$
  }

  getPerformanceMetrics(teamIds: number[], startDate: Date | null, endDate: Date | null) {
    const startDateString = startDate !== null ? startDate.toISOString() : null
    const endDateString = endDate !== null ? endDate.toISOString() : null
    const params = {
      ...(teamIds.length > 0 ? { teamIds } : {}),
      ...(startDateString !== null ? { startDateString } : {}),
      ...(endDateString !== null ? { endDateString } : {}),
    }
    return this.query({
      queryKey: queryKeys.metrics.performance(teamIds, startDateString, endDateString),
      queryFn: () => this.httpClient.get<PerformanceMetric[]>('/api/metrics/performance', { params }),
    }).result$
  }

  getActivityMetrics(teamIds: number[], startDate: Date, endDate: Date, precision: ActivityPrecision) {
    const startDateIso = startDate.toISOString()
    const endDateIso = endDate.toISOString()
    const params = {
      ...(teamIds.length > 0 ? { teamIds } : {}),
      startDate: startDateIso,
      endDate: endDateIso,
      precision,
    }
    return this.query({
      queryKey: queryKeys.metrics.activity(teamIds, startDateIso, endDateIso, precision),
      queryFn: () => this.httpClient.get<ActivityMetricsResult>('/api/metrics/activity', { params }),
    }).result$
  }

  getSlaMetrics(teamIds: number[], month: string) {
    const params = {
      ...(teamIds.length > 0 ? { teamIds } : {}),
      month,
    }
    return this.query({
      queryKey: queryKeys.metrics.sla(teamIds, month),
      queryFn: () => this.httpClient.get<SlaMetricsResult>('/api/metrics/sla', { params }),
    }).result$
  }

  exportSlaMetrics(teamIds: number[], month: string): Observable<void> {
    const params = {
      ...(teamIds.length > 0 ? { teamIds } : {}),
      month,
    }
    return this.httpClient.get('/api/metrics/sla/export', { params, responseType: 'blob', headers: generateHeadersWithTimezone() }).pipe(
      tap((data) => {
        this.downloadsService.downloadData(data, `sla-dashboard-${month}.pdf`, 'pdf')
        this.toastService.show('MEETINGS.EXPORT_COMPLETED')
      }),
      catchError((error) => {
        // A 4xx is the caller's own doing and a 503 means the renderer is busy, both of which the retry advice
        // in the toast already covers. Only an unexpected server failure is worth an error.
        const isExpected = error instanceof HttpErrorResponse && (error.status < 500 || error.status === 503)
        const message = 'Exporting the SLA dashboard failed.'
        if (isExpected) {
          this.loggerService.warn(message, error)
        } else {
          this.loggerService.error(message, error)
        }
        this.toastService.show('MEETINGS.EXPORT_FAILED')
        return EMPTY
      }),
      map(() => void 0)
    )
  }

  /**
   * The caller's client measured against its subscription. Answers null where support has recorded
   * no contract, which is what leaves the subscription period off the dashboard.
   */
  getSubscriptionUsage() {
    return this.query({
      queryKey: queryKeys.metrics.subscriptionUsage(),
      queryFn: () => this.httpClient.get<SubscriptionUsageResult | null>('/api/metrics/subscription-usage'),
    }).result$
  }

  getMeetingRoomMetrics(teamIds: number[]) {
    const params = {
      ...(teamIds.length > 0 ? { teamIds } : {}),
    }
    return this.query({
      queryKey: queryKeys.metrics.meetingRoomMetrics(teamIds),
      queryFn: () => this.httpClient.get<MeetingRoomMetric[]>('/api/metrics/meeting-rooms', { params }),
    }).result$
  }

  exportCompletedMeetings(
    userId: number | null,
    teamIds: number[],
    exclusionFilters: string[],
    viewFilter: string | null,
    timeInterval: TimeInterval,
    nameQuery: string | null,
    sortField: string | null,
    sortDirection: string | null,
    includeAudioMetrics: boolean,
    includeDetailedAudioMetrics: boolean
  ): Observable<void> {
    const fileName = `completed-meetings-from-${timeInterval.from?.toISOString()}${timeInterval.to ? `-to-${timeInterval.to.toISOString()}` : ''}.csv`
    const params = {
      ...(userId ? { userId: userId.toString() } : {}),
      ...(teamIds !== null && teamIds.length > 0 ? { teamIds: teamIds } : {}),
      ...(exclusionFilters.length > 0 ? { exclusionFilters } : {}),
      ...(timeInterval.from ? { fromDateString: timeInterval.from.toISOString() } : {}),
      ...(timeInterval.to ? { toDateString: timeInterval.to.toISOString() } : {}),
      ...(viewFilter ? { viewFilter } : {}),
      ...(nameQuery ? { nameQuery } : {}),
      ...(sortField ? { sortField } : {}),
      ...(sortDirection ? { sortDirection } : {}),
      includeAudioMetrics: includeAudioMetrics ? 'true' : 'false',
      includeDetailedAudioMetrics: includeDetailedAudioMetrics ? 'true' : 'false',
    }
    return this.httpClient
      .get(`/api/metrics/completed-meetings/export`, {
        params,
        responseType: 'blob',
        headers: generateHeadersWithTimezone(),
      })
      .pipe(
        tap((data) => {
          this.downloadsService.downloadData(data, fileName, 'csv')
          this.toastService.show('MEETINGS.EXPORT_COMPLETED')
        }),
        catchError((error) => {
          console.error('Export failed:', error)
          this.toastService.show('MEETINGS.EXPORT_FAILED')
          return EMPTY
        }),
        map(() => void 0)
      )
  }
}
